/* eslint-disable react/prop-types */
import { useState } from 'react';
import { Table, DatePicker, Row, Col, Card, Empty, Button, Tooltip, Grid } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFetchChargePointCount } from './Hook/Index';
import { useTranslation } from 'react-i18next';
import { downloadExcel } from '../../utils/downloadExcel';
import PropTypes from 'prop-types';
import ChargePointCharts from './ChargePointCharts';

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

export default function ChargePointCountTab({ operatorId, active = false }) {
  const { t } = useTranslation('reportPage');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ]);
  const { data, isLoading } = useFetchChargePointCount(
    {
      operatorId,
      fromDate: dateRange[0],
      toDate: dateRange[1],
    },
    { enabled: active }
  );

  const handleDateChange = (dates) => {
    setDateRange(
      dates
        ? [
            dayjs(dates[0]).format('YYYY-MM-DD'),
            dayjs(dates[1]).format('YYYY-MM-DD'),
          ]
        : []
    );
  };

  const handleExportCsv = async () => {
    const operatorRows = data?.chargerCountByOperator;
    const rows = Array.isArray(operatorRows) && operatorRows.length
      ? operatorRows.map((record) => ({
          OperatorName: record.operatorName,
          ChargerCount: record.chargerCount,
        }))
      : (data?.series || []).map((record) => ({
          Date: record.date,
          TotalChargers: record.totalChargers,
          Added: record.added,
        }));
    if (!rows.length) return;

    await downloadExcel(rows, 'ChargePointCount.xlsx');
  };

  const columns = [
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      width: isMobile ? 150 : 200,
    },
    {
      title: t('chargerCount'),
      dataIndex: 'chargerCount',
      key: 'chargerCount',
      align: 'center',
      width: isMobile ? 100 : 120,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-6">
        <RangePicker
          onChange={handleDateChange}
          className={`${isMobile ? 'w-full' : 'max-w-[300px]'}`}
          value={
            dateRange.length === 2
              ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
              : null
          }
          presets={[
            { label: t('today'), value: [dayjs(), dayjs()] },
            { label: t('thisWeek'), value: [dayjs().startOf('week'), dayjs().endOf('week')] },
            { label: t('thisMonth'), value: [dayjs().startOf('month'), dayjs().endOf('month')] },
            { label: t('last30Days'), value: [dayjs().subtract(30, 'days'), dayjs()] },
          ]}
        />
        <Tooltip title={t('exportCsv')}>
          <Button
            onClick={handleExportCsv}
            icon={<DownloadOutlined />}
            disabled={isLoading || (!data?.chargerCountByOperator?.length && !data?.series?.length)}
            className={isMobile ? 'w-full sm:w-auto' : ''}
          />
        </Tooltip>
      </div>

      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} className="mb-4">
        <Col span={isMobile ? 12 : 8}>
          <Card size="small" title={t('totalChargers')} loading={isLoading}>
            <div className="text-2xl text-center">{data?.totalChargers || 0}</div>
          </Card>
        </Col>
        {dateRange.length > 0 && (
          <Col span={isMobile ? 12 : 8}>
            <Card size="small" title={t('totalChargersAdded')} loading={isLoading}>
              <div className="text-2xl text-center">{data?.totalChargersAdded || 0}</div>
            </Card>
          </Col>
        )}
        <Col span={isMobile ? 12 : 8}>
          <Card size="small" title={t('newLocations')} loading={isLoading}>
            <div className="text-2xl text-center">{data?.newLocations || 0}</div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} className="mb-4">
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('onlineRate')} loading={isLoading}>
            <div className="text-2xl text-center text-emerald-700">
              {data?.insights?.onlineRate ?? 0}%
            </div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('onlineChargers')} loading={isLoading}>
            <div className="text-2xl text-center">{data?.insights?.onlineCount || 0}</div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('offlineChargers')} loading={isLoading}>
            <div className="text-2xl text-center">{data?.insights?.offlineCount || 0}</div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('faultedChargers')} loading={isLoading}>
            <div className="text-2xl text-center text-red-600">
              {data?.insights?.faultedCount || 0}
            </div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('acChargers')} loading={isLoading}>
            <div className="text-2xl text-center">{data?.insights?.acCount || 0}</div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('dcChargers')} loading={isLoading}>
            <div className="text-2xl text-center">{data?.insights?.dcCount || 0}</div>
          </Card>
        </Col>
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('maintenanceChargers')} loading={isLoading}>
            <div className="text-2xl text-center">
              {data?.insights?.maintenanceCount || 0}
            </div>
          </Card>
        </Col>
      </Row>

      <ChargePointCharts
        series={data?.series}
        operatorBreakdown={data?.chargerCountByOperator}
        byStatus={data?.byStatus}
        byPowerType={data?.byPowerType}
        byConnectorType={data?.byConnectorType}
        totalChargersAdded={data?.totalChargersAdded || 0}
        loading={isLoading}
      />

      {!operatorId && Array.isArray(data?.chargerCountByOperator) && data.chargerCountByOperator.length > 0 && (
        <div className="overflow-x-auto mt-4">
          <Table
            columns={columns}
            dataSource={data.chargerCountByOperator}
            loading={isLoading}
            rowKey="_id"
            pagination={false}
            scroll={{ x: isMobile ? 300 : undefined }}
          />
        </div>
      )}
      {!operatorId && !data?.chargerCountByOperator?.length && !isLoading && !data?.series?.length && (
        <Empty description={t('noDataAvailable')} />
      )}
    </div>
  );
}

ChargePointCountTab.propTypes = {
  operatorId: PropTypes.string,
  active: PropTypes.bool,
};
